import TaskCode from './TaskCode'
import ProjectInstallationType from './ProjectInstallationType'

class AppointmentType {

    static INSTALLATION = 'installation' //'BOOKKUNDE' & 'CPE' tasks
    static INSPECTION = 'inspection' //'KONBESIGT' & 'FIBERKONSU' tasks
    static TECHNICIAN = 'technician' //'BOOKKUNDE' & 'UDVID' tasks
    static SPLICE_UUB = 'splice_uub' //'PATCH' & 'KSFIBER task for a hub
    static PATCH = 'patch' //'PATCH' task without 'CPE' task
    static KSFIBER = 'ksfiber' //'KSFIBER' task
    static TICKET = 'ticket' //TroubleTickets
    static CUSTOM = 'Custom' //Used for appointments without tasks (yellow and red on timeline) //I don't know why this is capitalized, but I can't be bothered to 'fix' it
    static UNKNOWN = 'unknown'

    static DEFAULT = this.INSTALLATION //Default appointment type
    
    static AllArray = [ //Array of all the types from above
        this.INSTALLATION, this.INSPECTION, this.TECHNICIAN, this.SPLICE_UUB, this.PATCH, this.KSFIBER, this.TICKET, this.CUSTOM, this.UNKNOWN
    ]

    static DropdownOptions = [ //Array of objects, with 'value' and 'text' properties, for dropdown options etc
        { value: this.INSTALLATION, text: this.translate(this.INSTALLATION) },
        { value: this.INSPECTION, text: this.translate(this.INSPECTION) },
        { value: this.TECHNICIAN, text: this.translate(this.TECHNICIAN) },
        { value: this.SPLICE_UUB, text: this.translate(this.SPLICE_UUB) },
        { value: this.PATCH, text: this.translate(this.PATCH) },
        { value: this.KSFIBER, text: this.translate(this.KSFIBER) },
        { value: this.TICKET, text: this.translate(this.TICKET) },
        // { value: this.CUSTOM, text: this.translate(this.CUSTOM) } //Custom is not an option in the dropdown
    ]

    static TaskRoles = { //The role a task plays in the appointment
        book: 'BOOK', //The task that should be completed when an appointment has been made
        primary: 'PRIMARY', //The task that the appointment is about
        other: 'OTHER' //Any other tasks that should also be required to be closed before the appointment can be considered done
    }

    //Model of each AppointmentType, with their expected tasks (Book, Primary, Other), and weather an expected task is required for the appointment to be valid:

    // Old order:
    //   installation
    //   inspection
    //   technician
    //   patch
    //   ticket
    //   Custom

    static allowCustomerCommunication = (type) => {
        switch (type) {
            case this.INSTALLATION: return true
            case this.INSPECTION: return true
            case this.TECHNICIAN: return true
            case this.SPLICE_UUB: return false
            case this.PATCH: return false
            case this.KSFIBER: return false
            case this.TICKET: return true
            case this.CUSTOM: return true
            default: return false
        }
    }
    
    static AppointmentTasks = { 
        ticket: {
            book: null,
            projectInstallationType: [ProjectInstallationType.TICKET],
            primary: {
                taskCode: TaskCode.TICKET,
                required: true
            },
            other: []
        },
        inspection: {
            projectInstallationType: [
                ProjectInstallationType.INSTALLATIONSPROJEKT,
                ProjectInstallationType.INSTALLATIONSPROJEKTOPSAMLING,
                ProjectInstallationType.EFTERTILSLUTNING,
                ProjectInstallationType.KONVERTERING
            ],
            book: {
                taskCode: TaskCode.BOOKBESIGT,
                required: false
            },
            primary: {
                taskCode: TaskCode.FIBERKONSU,
                required: true
            },
            other: []
        },
        installation: {
            projectInstallationType: [
                ProjectInstallationType.INSTALLATIONSPROJEKT,
                ProjectInstallationType.INSTALLATIONSPROJEKTOPSAMLING,
                ProjectInstallationType.EFTERTILSLUTNING,
                ProjectInstallationType.KONVERTERING
            ],
            book: {
                taskCode: TaskCode.BOOKKUNDE,
                required: true
            },
            primary: {
                taskCode: TaskCode.CPE,
                required: true
            },
            other: [
                {
                    taskCode: TaskCode.PATCH,
                    required: false
                },
                {
                    taskCode: TaskCode.BLAES,
                    required: false
                },
                {
                    taskCode: TaskCode.SPLIDS,
                    required: false
                },
            ]
        },
        technician: {
            projectInstallationType: [
                ProjectInstallationType.INSTALLATIONSPROJEKT,
                ProjectInstallationType.INSTALLATIONSPROJEKTOPSAMLING,
                ProjectInstallationType.EFTERTILSLUTNING,
                ProjectInstallationType.KONVERTERING
            ],
            book: {
                taskCode: TaskCode.BOOKKUNDE,
                required: true
            },
            primary: {
                taskCode: TaskCode.UDVID,
                required: true
            },
            other: []
        },
        splice_uub: {
            projectInstallationType: [ProjectInstallationType.SKABSPROJEKT],
            book: null,
            primary: {
                taskCode: TaskCode.PATCH,
                required: true
            },
            other: [
                {
                    taskCode: TaskCode.KSFIBER,
                    required: false
                }
            ],
        },
        patch: {
            projectInstallationType: [
                ProjectInstallationType.INSTALLATIONSPROJEKT,
                ProjectInstallationType.INSTALLATIONSPROJEKTOPSAMLING,
                ProjectInstallationType.EFTERTILSLUTNING,
                ProjectInstallationType.KONVERTERING
            ],
            book: null,
            primary: {
                taskCode: TaskCode.PATCH,
                required: true
            },
            other: [],
        },
        ksfiber: {
            projectInstallationType: [
                ProjectInstallationType.INSTALLATIONSPROJEKT,
                ProjectInstallationType.INSTALLATIONSPROJEKTOPSAMLING,
                ProjectInstallationType.EFTERTILSLUTNING,
                ProjectInstallationType.KONVERTERING
            ],
            book: null,
            primary: {
                taskCode: TaskCode.KSFIBER,
                required: true
            },
            other: [],
        },
        Custom: { //With a captical 'C' for some reason...
            book: null,
            primary: null,
            projectInstallationType: [],
            other: []
        }
    }

    /**
     * Takes an appointment type, and returns an array of task codes for that type of appointment, optionally only returning the required tasks
     * @param {String} type one of the types defined at the top of this file
     * @param {Boolean} onlyRequired If true, only required tasks are returned, if false, all tasks are returned
     * @returns {Array} Array of task codes, required for the appointment type
     */
    static taskCodesForType(type, onlyRequired = false) {
        let taskModel = this.AppointmentTasks[type]
        let taskCodes = []

        // Check if there is a book task, and if onlyRequired is true, check if the book task is required
        if (taskModel.book && (!onlyRequired || taskModel.book.required)) { 
            taskCodes.push(taskModel.book.taskCode)
        }

        // Check if there is a primary task, and if onlyRequired is true, check if the primary task is required
        if (taskModel.primary && (!onlyRequired || taskModel.primary.required)) { 
            taskCodes.push(taskModel.primary.taskCode)
        }
        for (let task of taskModel.other) {
            // Check if onlyRequired is true, and if the task is required
            if (!onlyRequired || task.required) {
                taskCodes.push(task.taskCode)
            }
        }
        return taskCodes
    }

    /**
     * Takes an array of task codes, and returns an array of appointment types where all/any Required tasks are present in the task code array
     * @param {*} taskCodes 
     * @param {*} ignoreMissingRequiredTasks 
     */
    static appointmentTypesFromTaskCodes(taskCodes, projectType, ignoreMissingRequiredTasks = false) {
        let appointmentTypes = []
        
        for (let type in this.AppointmentTasks) {
            if (type === this.CUSTOM) continue //Custom is not releavant if there are any tasks at all

            if (this.AppointmentTasks[type].projectInstallationType.includes(projectType) === false) {
                continue; //Skip appointment types that are not relevant for the current project installation type
            }

            let requiredTaskCodes = this.taskCodesForType(type, !ignoreMissingRequiredTasks) //When ignoring missing required tasks, include tasks that are not required
            
            let anyRequiredTasksPresent = false
            let allRequiredTasksPresent = true
            for (let code of requiredTaskCodes) {
                if (taskCodes.includes(code)) {
                    anyRequiredTasksPresent = true
                } else {
                    allRequiredTasksPresent = false
                }
            }
            if (allRequiredTasksPresent || (anyRequiredTasksPresent && ignoreMissingRequiredTasks)) {
                appointmentTypes.push(type)
            }
        }
        return appointmentTypes
    }

    /**
     * Takes one of the standard AppointmentTypes, and 
     * @param {String} type one of the types defined at the top of this file
     * @returns {String} one or two Danish words, describing the type of appointment, in all lowercase
     */
    static translate(type) {
        switch (type) {
            case this.INSTALLATION:
                return 'installation'
            case this.INSPECTION:
                return 'besigtigelse'
            case this.TECHNICIAN:
                return 'montørhjælp'
            case this.SPLICE_UUB:
                return 'splids teknikhus'
            case this.PATCH:
                return 'patch'
            case this.KSFIBER:
                return 'kvalitetssikring'
            case this.TICKET:
                return 'driftsag'
            case this.CUSTOM:
                return 'brugerdefineret aftale'
            default:
                console.error(`Did not recognize AppointmentType '${type}', returning value as is`)
                return type
        }
    }

    /**
     * Takes array of tasks, and returns a compacted version of only the relevant tasks, ready to be saved in Firestore
     * @param {Array} taskList Array of tasks associated with a customer, to be filtered for association with specific appointment
     * @param {String} appType One of the types of appointments in AppointmentType.AllArray
     * @returns {Array} Array of tasks in compact version, ready to be saved to firestore
     * @throws {Error}
     */
    static relevantCompactTasks(taskList, appType = this.INSTALLATION, ignoreBook = false) { //TODO: Throw an error or something, if there are more than 1 tasks matching the code for Book or Primary task
        let taskModel = this.AppointmentTasks[appType] //The model describes the kinds of tasks expected to be part of this type of appointment
        let bookTask = taskModel.book ? taskList.find((task) => (task.code || this.TICKET) == taskModel.book.taskCode) : null // If the model specifies a booking task, find that task in taskList
        let primaryTask = taskModel.primary ? taskList.find((task) => (task.code || this.TICKET) == taskModel.primary.taskCode) : null // If the model specifies a primary task, find that task in taskList
        if ((!primaryTask && taskModel.primary && taskModel.primary.required)) { //If the model specifies a primary task, but it is not present in taskList, throw an error
            throw new Error('A primary task was required, but not present in taskList (AppointmentType.relevantCompactTasks())')
        }
        if (!bookTask && taskModel.book && taskModel.book.required && !ignoreBook) { //If the model specifies a booking task, but it is not present in taskList, throw an error
            throw new Error('A Booking-task was required, but not present in taskList (AppointmentType.relevantCompactTasks())')  
        }

        let compactList = [] //Initialize the array to be returned
        if (bookTask) { //If there is a bookTask, add the compact version to the compactList
            compactList.push({
                Role: this.TaskRoles.book,
                Code: bookTask.code || null,
                Id: bookTask.id || null,
                Number: bookTask.number || null
            })
        }
        if (primaryTask) { //If there is a primaryTask, add the compact version to the compactList
            compactList.push({
                Role: this.TaskRoles.primary,
                Code: primaryTask.code || null,
                Id: primaryTask.id || null,
                Number: primaryTask.number || null
            })
        }

        for (let modelTask of taskModel.other) { //Loop through the "other" tasks in the model
            let foundTask = taskList.find((task) => (task.code || this.TICKET) == modelTask) //Find the task with the matching code in the taskList
            if (!foundTask) { //If there was no task to match the models expectations
                if (modelTask.required) { //Check if that task is required in the model, and throw an error if it is
                    throw new Error(`A required task code (${modelTask.taskCode}) was not found in tasklist (AppointmentType.relevantCompactTasks)`)
                }
                continue; //If a non-required task from the model was not found, skip it
            } else {
                compactList.push({ //Push each "other" task to the array to be returned
                    Role: this.TaskRoles.other,
                    Code: foundTask.code,
                    Id: foundTask.id,
                    Number: foundTask.number
                })
            }
        }
        console.log(compactList)
        return compactList //{Array} of compacted tasks
    }

}

export default AppointmentType